/**
 * AuthProvider — Authentication Context Provider
 *
 * Manages auth state, modal visibility, and auth hydration from the BFF.
 *
 * W3.1/W3.2 (auth-debt epic):
 * - Auth state is ONE discriminated union (`AuthSession`) — the former
 *   trio of independent `useState`s could represent "authenticated with
 *   no user"; that state is now uncompilable. The convenience fields
 *   (`isAuthenticated`/`user`) are DERIVED from the union, never stored.
 *   (The Messages badge count is no longer here — R5 moved it to its own
 *   `/api/conversations/unread-count` query.)
 * - `UserProfile` is the canonical `@letsgethome/lib/auth/types` shape —
 *   the app-local re-declaration (which silently dropped roles/
 *   permissions) is gone.
 * - Hydration failures are caught (the former bare `.then()` left an
 *   unhandled rejection and a silently signed-out UI).
 * - The provider honours the server auth-middleware's `?auth=1&redirect=`
 *   contract (`auth-modal-params.ts`): open the modal on load, resume the
 *   original protected navigation after sign-in.
 */
import { useRevalidate, useRouter, useSearchParams } from '@/shared/i18n/routing';
import {
  AUTH_MODAL_PARAM,
  AUTH_MODAL_PARAM_VALUE,
  AUTH_REDIRECT_PARAM,
} from '@/shared/lib/auth-modal-params';
import { bff, bffGet } from '@/shared/lib/bff';
import { clientLogger } from '@/shared/lib/client-logger';
import type { UserProfile } from '@letsgethome/lib/auth/types';
import { createSafeContext } from '@letsgethome/lib/react/create-safe-context';
import { internalPath } from '@letsgethome/routes';
import type { GoogleClientId } from '@letsgethome/types/branded';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export type { UserProfile };

/**
 * The auth session as a discriminated union — `authed` STRUCTURALLY
 * carries the user (and the hydrated badge count), so "authenticated with
 * nobody" and "user without a session" cannot be represented.
 */
export type AuthSession =
  | { status: 'loading' }
  | { status: 'anon' }
  | { status: 'authed'; user: UserProfile };

export interface AuthContextValue {
  /** The session union — narrow on `status` for exhaustive rendering. */
  session: AuthSession;
  /** Derived: `session.status === 'authed'`. */
  isAuthenticated: boolean;
  /** Derived: the authed user, or `null`. */
  user: UserProfile | null;
  isModalOpen: boolean;
  /**
   * The gate a signed-in-only affordance asks before acting. `true` means act
   * now. `false` means the gate has taken the action over: it will `resume`
   * once the visitor is signed in — after the auth modal, or, while the session
   * is still RESOLVING after hydration, as soon as it resolves signed in. See
   * the provider's `requireAuth` for the measured defect.
   */
  requireAuth: (resume?: () => void) => boolean;
  /**
   * Open the sign-in modal. An optional `resume` closure is replayed on
   * successful sign-in (SV-W3) — e.g. the save a signed-out user just clicked.
   * The page tree stays mounted (the modal is a portal sibling of the route),
   * so the closure's component is still alive when it fires.
   */
  openAuthModal: (resume?: () => void) => void;
  closeAuthModal: () => void;
  /**
   * Commit a successful sign-in. RESOLVES only once the route's loaders have
   * re-run with the new auth cookie (or the resume navigation has landed) —
   * the modal is still up until then, so the stale signed-out page underneath
   * is never uncovered. Awaiting it is optional for callers; the ordering it
   * guarantees is not theirs to get right.
   */
  onAuthSuccess: (user?: UserProfile) => Promise<void>;
  logout: () => void;
  googleClientId?: GoogleClientId;
}

// ----------------------------------------------------------------------------
// Context + Hooks
// ----------------------------------------------------------------------------

const [AuthContextProvider, useAuth] = createSafeContext<AuthContextValue>('Auth');

/**
 * The attribute `<html>` carries with the live session's `status`
 * (`loading` | `anon` | `authed`), stamped by the provider on every change.
 *
 * ★ PUBLISHED SO THE PREMISE CAN BE MEASURED. `[data-hydrated]` says the
 * client root committed; it says nothing about whether this tab yet KNOWS who
 * is signed in, which is a later fact (`/api/auth/me` after hydration). A
 * spec that clicks a gated control on the strength of the hydration stamp
 * alone is asserting a premise nothing on the page stated — which is exactly
 * the click the 2026-09-13 prod gate refused three times. `web/e2e/helpers`
 * mirrors the spelling (`html[data-session=…]`) the way it mirrors
 * `HYDRATED_ATTRIBUTE`; the react test beside this provider holds the two
 * equal.
 */
export const SESSION_ATTRIBUTE = 'data-session';

export { useAuth };

export function useAuthGate() {
  const { session, isAuthenticated, user, requireAuth, openAuthModal, onAuthSuccess, logout } =
    useAuth();

  return {
    session,
    isAuthenticated,
    user,
    requireAuth,
    openAuthModal,
    onAuthSuccess,
    logout,
  };
}

// ----------------------------------------------------------------------------
// Provider
// ----------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
  googleClientId?: GoogleClientId;
}

export function AuthProvider({ children, googleClientId }: AuthProviderProps) {
  const router = useRouter();
  const { revalidate } = useRevalidate();
  const searchParams = useSearchParams();
  const [session, setSession] = useState<AuthSession>({ status: 'loading' });

  const hydrate = useCallback(() => {
    bffGet('/api/auth/me')
      .then((state) => {
        // The seam contract is itself a discriminated union, so this
        // narrows without any `user ?? null` coalescing.
        setSession(
          state.isAuthenticated ? { status: 'authed', user: state.user } : { status: 'anon' }
        );
      })
      .catch((error: unknown) => {
        // A failed hydration is a signed-out UI, but never a silent one
        // (the former bare `.then()` surfaced as an unhandled rejection).
        clientLogger.warn(
          { component: 'auth', error: error instanceof Error ? error : new Error(String(error)) },
          'Auth hydration failed; rendering signed-out'
        );
        setSession({ status: 'anon' });
      });
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Server middleware contract (W3.1): a protected route redirected here
  // with `?auth=1&redirect=<path>` — open the modal on load and stash the
  // sanitized resume path for `onAuthSuccess`. Initial-load params only
  // (the middleware always lands a fresh document); the ref survives the
  // modal lifecycle without re-triggering on param churn.
  const initialParams = useRef(searchParams);
  const resumePathRef = useRef<string | null>(null);
  // SV-W3: a client-side action to replay after sign-in (e.g. the pending
  // save). Distinct from `resumePathRef` (the server-redirect protected-route
  // path); a normal in-place gate sets this, never a navigation.
  const pendingResumeRef = useRef<(() => void) | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(
    () => initialParams.current.get(AUTH_MODAL_PARAM) === AUTH_MODAL_PARAM_VALUE
  );
  useEffect(() => {
    if (initialParams.current.get(AUTH_MODAL_PARAM) === AUTH_MODAL_PARAM_VALUE) {
      resumePathRef.current = initialParams.current.get(AUTH_REDIRECT_PARAM);
    }
  }, []);

  const openAuthModal = useCallback((resume?: () => void) => {
    pendingResumeRef.current = resume ?? null;
    setIsModalOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => {
    // Dismissed without signing in — drop the pending action so it never fires
    // on some later, unrelated sign-in.
    pendingResumeRef.current = null;
    setIsModalOpen(false);
  }, []);

  /**
   * A gated click made while the session was still RESOLVING: the action the
   * gate took over, to be resumed (signed in) or handed to the modal (anon)
   * the moment the answer arrives. `null` when nothing is waiting.
   */
  const deferredGateRef = useRef<{ resume: (() => void) | undefined } | null>(null);

  /**
   * ★ THE THIRD ANSWER (2026-09-13). This gate had two answers — `true` for
   * `authed`, otherwise open the modal — and `loading` fell into the second:
   * a signed-in visitor who clicked a gated control between the hydration
   * stamp and `/api/auth/me` resolving was shown the LOGIN modal and went
   * nowhere. Measured on staging in the prod-promote smoke gate
   * (`prod-promote-sd9s5`, 15:33Z): `inquiry-gate.spec.ts` clicked the contact
   * CTA right after `[data-hydrated]`, `waitForURL` timed out at 20 s, three
   * attempts of three, and prod stayed unpinned on a test that was right about
   * the product. While the session is resolving the gate now DEFERS: it takes
   * the click, and the effect below resumes it if the answer is `authed` or
   * opens the modal with it if the answer is `anon`. Nothing is guessed in
   * either direction.
   */
  const requireAuth = useCallback(
    (resume?: () => void): boolean => {
      if (session.status === 'authed') return true;
      if (session.status === 'loading') {
        deferredGateRef.current = { resume };
        return false;
      }
      openAuthModal(resume);
      return false;
    },
    [session.status, openAuthModal]
  );

  useEffect(() => {
    if (session.status === 'loading') return;
    const deferred = deferredGateRef.current;
    if (deferred === null) return;
    deferredGateRef.current = null;
    if (session.status === 'authed') {
      deferred.resume?.();
    } else {
      openAuthModal(deferred.resume);
    }
  }, [session.status, openAuthModal]);

  // The live session, published on `<html>` for whoever needs to MEASURE it
  // rather than assume it — see `SESSION_ATTRIBUTE`.
  useEffect(() => {
    document.documentElement.setAttribute(SESSION_ATTRIBUTE, session.status);
  }, [session.status]);

  /**
   * The sign-in commit, and the reason it is `async`.
   *
   * The page under the modal renders from LOADER data — server truth for the
   * request that produced it, i.e. taken while the visitor was still anonymous
   * — and this provider holds the live session. Closing the modal FIRST
   * uncovers the guest view and leaves it up for however long the bridge takes;
   * the bridge used to be `router.refresh()` (`history.go(0)`), which was
   * neither typed nor awaitable, so "how long" was the browser's business and
   * nobody's to test. That is the LET-88 report's whole surface area.
   *
   * So the order is inverted and the wait is real: re-run the loaders with the
   * now-set cookie, AWAIT the answer, and only then take the modal down. On the
   * resume path the same argument applies to the navigation — the destination's
   * loaders run as part of it, so awaiting the push is awaiting the same fact.
   * `useAuthState()` is the backstop that makes the guarantee hold for the
   * paths that do not come through here at all.
   */
  const onAuthSuccess = useCallback(
    async (newUser?: UserProfile): Promise<void> => {
      if (newUser) {
        setSession({ status: 'authed', user: newUser });
      } else {
        // Success without a profile payload — re-hydrate rather than
        // fabricate an authed session with no user (unrepresentable now).
        hydrate();
      }
      // Replay the in-place action first (the auth cookie is now set, so the
      // BFF call it fires is authenticated). The overlay in useSaveState keeps
      // the optimistic result stable across the revalidation below.
      const pendingResume = pendingResumeRef.current;
      pendingResumeRef.current = null;
      pendingResume?.();
      const resume = resumePathRef.current;
      resumePathRef.current = null;
      const target = resume === null ? null : internalPath(resume);
      if (target) {
        await router.push(target);
      } else {
        await revalidate();
      }
      setIsModalOpen(false);
    },
    [router, revalidate, hydrate]
  );

  const logoutMutation = useMutation({
    mutationFn: () => bff('/api/auth/logout'),
    onMutate: () => setSession({ status: 'anon' }),
    // Same bridge in the other direction: the cookie is gone, so the loaders
    // must re-run for the page to stop rendering the signed-in view. Until
    // they do, `useAuthState()` reports `revalidating` rather than authed data.
    onSettled: () => revalidate(),
  });

  const logout = useCallback(() => logoutMutation.mutate(), [logoutMutation]);

  const value: AuthContextValue = {
    session,
    isAuthenticated: session.status === 'authed',
    user: session.status === 'authed' ? session.user : null,
    isModalOpen,
    requireAuth,
    openAuthModal,
    closeAuthModal,
    onAuthSuccess,
    logout,
    googleClientId,
  };

  return <AuthContextProvider value={value}>{children}</AuthContextProvider>;
}
